import ClearAllIcon from "@mui/icons-material/ClearAll";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Badge,
  Box,
  Card,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";
import { MatchFilter } from "../../libs/statsFilters";
import { useCityEscapeFiltersDialog } from "../../providers/CityEscapeFiltersDialogProvider";
import { convertFiltersToChips, FilterChipData } from "./filterChips";

interface FilterBarProps {
  filters: MatchFilter;
  sticky?: {
    top?: number;
    bottom?: number;
    zIndex?: number;
  };
  onClearAll?: () => void;
  onReset?: () => void;
  onEdit?: (filters: MatchFilter) => void;
}

/**
 * Renderizza un singolo chip
 */
const FilterChip: React.FC<{
  chipData: FilterChipData;
  onRemove: (chipData: FilterChipData) => void;
}> = ({ chipData, onRemove }) => (
  <Chip
    key={chipData.key}
    label={chipData.label}
    onDelete={(event) => onRemove(chipData)}
    variant="outlined"
    size="small"
    sx={{
      margin: 0.25,
      "& .MuiChip-deleteIcon": {
        fontSize: "18px",
      },
    }}
  />
);

/**
 * Renderizza la sezione Clear All se ci sono filtri
 */
const ClearAllButton: React.FC<{
  hasFilters: boolean;
  onClearAll?: () => void;
}> = ({ hasFilters, onClearAll }) => {
  if (!hasFilters || !onClearAll) {
    return null;
  }

  return (
    <Tooltip title="Azzera filtri">
      <IconButton
        onClick={onClearAll}
        color="secondary"
        size="small"
      >
        <ClearAllIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Renderizza il pulsante Reset per ripristinare i filtri di default
 */
const ResetButton: React.FC<{
  onReset?: () => void;
}> = ({ onReset }) => {
  if (!onReset) {
    return null;
  }

  return (
    <Tooltip title="Ripristina filtri di default">
      <IconButton
        onClick={onReset}
        color="primary"
        size="small"
      >
        <RestoreIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Renderizza il pulsante Edit per aprire il dialog di modifica
 */
const EditButton: React.FC<{
  onEdit?: (...args: any[]) => void;
}> = ({ onEdit }) => {
  if (!onEdit) {
    return null;
  }

  return (
    <Tooltip title="Modifica filtri">
      <IconButton
        onClick={onEdit}
        color="primary"
        size="small"
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

/**
 * Renderizza il messaggio quando non ci sono filtri
 */
const NoFiltersMessage: React.FC = () => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ fontStyle: "italic" }}
  >
    Nessun filtro attivo
  </Typography>
);

/**
 * Componente principale FilterBar
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  sticky,
  onClearAll,
  onReset,
  onEdit,
}) => {
  const chipData = useMemo(() => convertFiltersToChips(filters), [filters]);
  const hasFilters = chipData.length > 0;
  const filterDialog = useCityEscapeFiltersDialog();
  const theme = useTheme();

  const handleOpenDialog = () => {
    filterDialog.openModal({
      filters,
      onSave: onEdit,
    });
  };

  const onRemove = (chip: FilterChipData) => {
    chip.onRemove?.(filters);
    onEdit?.(filters);
  };

  const stickyStyles = useMemo(() => {
    if (!sticky) return {};
    let styles: React.CSSProperties = {
      position: "sticky",
      zIndex: sticky.zIndex ?? 2,
      backgroundColor: theme.palette.background.paper,
    };

    if (sticky.top !== undefined) {
      styles.top = sticky.top;
    } else if (sticky.bottom !== undefined) {
      styles.bottom = sticky.bottom;
    }
    return styles;
  }, [sticky, theme]);

  return (
    <Box width={"100%"} minWidth={"100%"} sx={stickyStyles}>
      <Card
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
        }}
        // variant="outlined"
        onClick={onEdit ? handleOpenDialog : undefined}
        elevation={4}
      >
        <Tooltip title={hasFilters ? `${chipData.length} filtri attivi` : "Nessun filtro attivo"}>
          <Badge
            badgeContent={chipData.length}
            color="primary"
            sx={{ mr: 2 }}
            invisible={!hasFilters}
          >
            <FilterListIcon
              color={hasFilters ? "primary" : "disabled"}
              fontSize="small"
            />
          </Badge>
        </Tooltip>

        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
          {hasFilters ? (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexWrap: "wrap",
                flex: 1,
                "& > *": {
                  margin: "2px !important",
                },
              }}
            >
              {chipData.map((chip) => (
                <FilterChip
                  key={chip.key}
                  chipData={chip}
                  onRemove={onRemove}
                />
              ))}
            </Stack>
          ) : (
            <NoFiltersMessage />
          )}

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <ClearAllButton hasFilters={hasFilters} onClearAll={onClearAll} />
            <ResetButton onReset={onReset} />
            <EditButton onEdit={onEdit ? handleOpenDialog : undefined} />
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default FilterBar;
